"use client";
import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Import CSS for the toast

type CartItem = { id: string; name: string; quantity: number; price: number; images: string[] };

type CartContextType = {
  cart: CartItem[];
  updateCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [toastShown, setToastShown] = useState<Set<string>>(new Set()); // Track shown toast for items

  // Load cart from localStorage initially
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("cart") || "[]");
    setCart(stored);
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Show toast whenever cart is updated (new item added or quantity updated)
  useEffect(() => {
    cart.forEach((item) => {
      // Check if item is not already in the toastShown Set
      if (!toastShown.has(item.id)) {
        toast.success(`${item.name} has been added to your cart!`, {
          position: "top-right",
          autoClose: 3000,
        });
        // Add the item ID to the toastShown Set to prevent duplicate toasts
        setToastShown((prev) => new Set(prev).add(item.id));
      }
    });
  }, [cart, toastShown]);

  // Update cart, either adding new items or updating existing ones
  const updateCart = (item: CartItem) => {
    setCart((prevCart) => {
      const existing = prevCart.find((p) => p.id === item.id);
      if (existing) {
        // If product exists, update quantity
        return prevCart.map((p) =>
          p.id === item.id
            ? { ...p, quantity: p.quantity + item.quantity, price: item.price, name: item.name, images: item.images }
            : p
        );
      } else {
        // If product doesn't exist, add it to the cart
        return [...prevCart, item];
      }
    });
  };

  // Remove item from cart by id
  const removeFromCart = (id: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
    // Reset the toastShown state when the cart changes
    setToastShown((prev) => {
      const newToastShown = new Set(prev);
      newToastShown.delete(id);
      return newToastShown;
    });
  };

  return (
    <CartContext.Provider value={{ cart, updateCart, removeFromCart }}>
      {children}
    </CartContext.Provider>
  );
};

// Hook for easier access to the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};
